import React, { useEffect, forwardRef, useImperativeHandle, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { equals, map, filter, pipe } from 'ramda';
import { Tab, Nav, Spinner } from 'react-bootstrap';
import { stringify } from 'query-string';
import { v4 as uuidv4 } from 'uuid';
import { debounce } from 'debounce';
import { FormattedMessage } from 'react-intl';
import { IAppState } from '../../../../common/models/store/IAppState';
import { fromISurveyQToIPreviewQ, removeRequiredQuestion, placeholders } from './utils';
import {
  fetchSurveyRoutine,
  fetchAttributesRoutine,
  fetchDefaultQuestionsRoutine,
  saveSurveyRoutine,
  checkSurveyNameRoutine,
  changeSurveyDescriptionRoutine,
  changeSurveyNameRoutine,
  fetchQuestionsRoutine
} from '../../routines';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { ISurveyQuestion } from '../../../../common/models/survey/ISurveyQuestion';
import { ISurveyData } from '../../../../common/models/survey/ISurveyData';
import { QuestionType } from '../../../../common/enums/QuestionType';
import SurveyBuilderTab from '../../components/SurveyBuilderTab';
import RegularButton from '../../../../components/RegularButton';
import { setObject, getObject } from '../../../../common/helpers/storageHelper';
import { Routes } from '../../../../common/enums/Routes';
import ModalWindow from '../../../../components/ModalWindow';
import { User } from '../../../../common/models/user/User';
import SaveAsPopup from '../SaveAsPopup';
import { ICreateSurvey } from '../../../../common/models/survey/ICreateSurvey';
import { IUpdateSurvey } from '../../../../common/models/survey/IUpdateSurvey';
import Icon from '../../../../components/Icon';
import IconButton from '../../../../components/IconButton';
import QuestionTab from '../QuestionTab';
import { IQuestionWithOwner } from '../../../../common/models/question/IQuestionWithOwner';
import AttributesCounter from '../AttributesCounter';
import { IQuestion } from '../../../../common/models/question/IQuestion';
import { handleEnter } from '../../../../common/helpers/keyboardHelper';
import { QuestionSection } from '../../../../common/enums/QuestionSection';
import styles from './styles.module.scss';

interface IProps {
  surveyId: string | null;
  isSaving: boolean;
  isNameTaken: boolean;
  name: string;
  description: string;
  selectedQuestions: ISurveyQuestion[];
  surveyData: ISurveyData;
  isSaved: boolean;
  isChecking: boolean;
  user: User;
  questions: Record<QuestionSection, IQuestionWithOwner[]>;
  fetchSurvey: IBindingCallback1<string>;
  fetchAttributes: IBindingAction;
  saveSurvey: IBindingCallback1<ICreateSurvey | IUpdateSurvey>;
  hideSurveyBuilder: IBindingAction;
  fetchDefaultQuestions: IBindingAction;
  changeName: IBindingCallback1<string>;
  changeDescription: IBindingCallback1<string>;
  checkSurveyName: IBindingCallback1<{ name: string; isSaveAs?: boolean; id?: string }>;
  fetchQuestions: IBindingCallback1<QuestionSection>;
}

export interface ISurveyBuilderHandle {
  confirmClose: IBindingAction;
}

const SurveyBuilder = forwardRef<ISurveyBuilderHandle, IProps>(({
  isSaving,
  surveyId,
  name,
  user,
  surveyData,
  isNameTaken,
  isSaved,
  isChecking,
  description,
  questions,
  selectedQuestions,
  saveSurvey,
  fetchSurvey,
  checkSurveyName,
  changeName,
  changeDescription,
  fetchAttributes,
  hideSurveyBuilder,
  fetchDefaultQuestions,
  fetchQuestions
}, ref) => {
  const containerRef = useRef(null);
  const [isConfirmCloseShown, setIsConfirmCloseShown] = useState(false);
  const [tab, setTab] = useState(QuestionSection.Questions);
  const [isSaveAsShown, setIsSaveAsShown] = useState(false);
  const [isConfirmShown, setIsConfirmShown] = useState(false);
  const [isNameUpdating, setIsNameUpdating] = useState(!surveyId);
  const [isDescrUpdating, setIsDescrUpdating] = useState(false);

  const isEqual = (
    equals(surveyData.surveyQuestions, selectedQuestions)
    && name.trim() === surveyData.name
    && description.trim() === (surveyData.description || '')
  );

  const saveDisabled = Boolean(isSaving
    || isNameTaken
    || (surveyData.id && isEqual)
    || (isChecking && !isSaveAsShown));

  const isOwner = surveyData.createdByUser?.id === user.id;

  const isNameChanged = (name.trim()
    && name.trim().toLowerCase() !== surveyData.name?.toLowerCase());

  const selectedRatingScale = pipe<ISurveyQuestion[], ISurveyQuestion[], IQuestion[]>(
    filter(({ question }: ISurveyQuestion) => question.type === QuestionType.RatingScale),
    map(({ question }) => question)
  )(selectedQuestions);

  const tabs = [QuestionSection.Questions, QuestionSection.SoftSkills];

  const onClose = () => ((surveyId && isEqual) ? hideSurveyBuilder() : setIsConfirmCloseShown(true));

  useImperativeHandle(ref, () => ({
    confirmClose() {
      onClose();
    }
  }));

  const onPreview = () => {
    const generatedId = uuidv4();
    const surveyPreviewData = getObject('surveyPreview');

    setObject('surveyPreview', {
      ...(surveyPreviewData || {}),
      [generatedId]: map(fromISurveyQToIPreviewQ, removeRequiredQuestion(selectedQuestions))
    });
    window.open(`${Routes.SurveyPreview}?${stringify({ id: generatedId })}`, '_blank');
  };

  const formSurveySaveData = () => ({
    name: name.trim(),
    description: description.trim() || undefined,
    surveyQuestions: removeRequiredQuestion(selectedQuestions)
  });

  const onConfirmSave = () => {
    saveSurvey({
      id: surveyData.id,
      ...formSurveySaveData()
    });
    setIsConfirmShown(false);
  };

  const onSave = () => saveSurvey(formSurveySaveData());

  const onSaveAs = () => setIsSaveAsShown(true);

  const onClickSave = () => {
    if (surveyData.id) {
      setIsConfirmShown(true);
    } else if (name.trim()) {
      onSave();
    } else {
      onSaveAs();
    }
  };

  useEffect(() => {
    if (surveyId) {
      fetchSurvey(surveyId);
    } else {
      fetchDefaultQuestions();
    }
    fetchAttributes();
    fetchQuestions(QuestionSection.Questions);
  }, []);

  useEffect(() => {
    if (isSaved) {
      hideSurveyBuilder();
    }
  }, [isSaved]);

  const onChangeTab = (type: QuestionSection) => {
    if (!questions[type].length) {
      fetchQuestions(QuestionSection.SoftSkills);
    }
    setTab(type);
  };

  const search = debounce((v: string) => checkSurveyName({ name: v.trim(), id: surveyData.id }), 500);

  useEffect(() => {
    search(name);
    return () => search.clear();
  }, [name]);

  return (
    <LoaderWrapper transparent loading={isSaving && !isSaveAsShown}>
      <ConfirmationModal
        isShown={isConfirmCloseShown}
        titleText={{ id: 'surveyBuilder.quit', defaultMessage: 'Quit survey editor' }}
        descriptionText={{
          id: 'surveyBuilder.confirmClose',
          defaultMessage: 'Are you sure you want to exit the survey editor window? All progress will be lost.'
        }}
        confirmBttnText={{ id: 'confirmDelete', defaultMessage: 'Yes, delete' }}
        cancelBttnText={{ id: 'keepBttn', defaultMessage: 'No, keep' }}
        onCancel={() => setIsConfirmCloseShown(false)}
        onConfirm={() => { setIsConfirmCloseShown(false); hideSurveyBuilder(); }}
      />
      <ConfirmationModal
        isShown={isConfirmShown}
        titleText={{ id: 'surveyBuilder.overwrite', defaultMessage: 'Overwrite survey' }}
        descriptionText={{
          id: surveyData.isUsed ? 'surveyBuilder.usedConfirmSave' : 'surveyBuilder.confirmSave',
          defaultMessage: `Are you sure you want to overwrite this survey? ${
            surveyData.isUsed ? 'It is currently in use.' : ''
          }`
        }}
        onConfirm={onConfirmSave}
        onCancel={() => setIsConfirmShown(false)}
        confirmBttnText={{ id: 'save', defaultMessage: 'Save' }}
      />
      <ModalWindow
        isShown={isSaveAsShown}
        onHide={() => setIsSaveAsShown(false)}
        hideCloseBttn
        contentClassName={styles.saveAsPopup}
      >
        <SaveAsPopup onCancel={() => setIsSaveAsShown(false)} />
      </ModalWindow>
      <div className={styles.surveyBuilder}>
        <Tab.Container activeKey={tab}>
          <header className={styles.sbHeader}>
            <div className={styles.sbInfoContainer}>
              <div className="main-icon">
                <Icon icon="survey" />
              </div>
              <div className={styles.sbInfo}>
                <div className="position-relative">
                  {!isSaveAsShown && isChecking && (
                    <Spinner className={styles.nameSpinner} animation="border" size="sm" />
                  )}
                  {isNameUpdating
                    ? (
                      <FormattedMessage {...placeholders.name}>
                        {placeholder => (
                          <input
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            placeholder={placeholder as string}
                            className={`${styles.surveyTitle} ${isNameTaken ? styles.inputError : ''}`}
                            value={name}
                            onKeyDown={event => handleEnter(event, () => setIsNameUpdating(false))}
                            onChange={({ target }) => changeName(target.value)}
                            onBlur={() => setIsNameUpdating(false)}
                          />
                        )}
                      </FormattedMessage>
                    )
                    : (
                      <>
                        <div className={`${styles.surveyTitle} ${styles.infoItem}`}>
                          <span className={`overflow-ellipsis ${name.trim() ? '' : 'secondary-text'}`}>
                            {name.trim() || <FormattedMessage {...placeholders.name} />}
                          </span>
                          <IconButton onClick={() => setIsNameUpdating(true)} icon={<Icon icon="pencil" />} />
                        </div>
                      </>
                    )}
                  {isNameTaken && (
                    <div className={`name-error ${styles.error}`}>
                      <Icon icon="warning" />
                      <FormattedMessage id="titleTaken" defaultMessage="Title already in use" />
                    </div>
                  )}
                </div>
                {isDescrUpdating
                  ? (
                    <FormattedMessage {...placeholders.description}>
                      {placeholder => (
                        <input
                          // eslint-disable-next-line jsx-a11y/no-autofocus
                          autoFocus
                          placeholder={placeholder as string}
                          value={description}
                          onChange={({ target }) => changeDescription(target.value)}
                          onKeyDown={event => handleEnter(event, () => setIsDescrUpdating(false))}
                          onBlur={() => setIsDescrUpdating(false)}
                        />
                      )}
                    </FormattedMessage>
                  )
                  : (
                    <div className={styles.infoItem}>
                      <span className={`overflow-ellipsis ${description.trim() ? '' : 'secondary-text'}`}>
                        {description.trim() || <FormattedMessage {...placeholders.description} />}
                      </span>
                      <IconButton onClick={() => setIsDescrUpdating(true)} icon={<Icon icon="pencil" />} />
                    </div>
                  )}
              </div>
            </div>
            <Nav>
              {tabs.map(val => (
                <SurveyBuilderTab onChangeTab={onChangeTab} key={val} type={val} />
              ))}
            </Nav>
          </header>
          <Tab.Content ref={containerRef} className={styles.sbBody}>
            {tabs.map(val => (
              <Tab.Pane key={val} eventKey={val}>
                <QuestionTab containerRef={containerRef} type={val} />
              </Tab.Pane>
            ))}
          </Tab.Content>
        </Tab.Container>
        <footer className="sb-footer">
          {tab === QuestionSection.SoftSkills && (
            <AttributesCounter selectedQuestions={selectedRatingScale} />
          )}
          <div className="sb-footer-bttns">
            <RegularButton
              onClick={onClose}
              textIntlId="cancel"
              defaultMessage="Cancel"
              variant="tertiary"
            />
            <RegularButton
              onClick={onPreview}
              textIntlId="preview"
              defaultMessage="Preview"
              variant="tertiary"
              icon={<Icon icon="preview" />}
            />
            {surveyData.id && (
              <RegularButton
                disabled={saveDisabled}
                onClick={isNameChanged ? onSave : onSaveAs}
                textIntlId={isOwner ? 'saveAs' : 'surveyBuilder.save'}
                defaultMessage={isOwner ? 'Save as' : 'Save survey'}
                icon={isOwner ? undefined : <Icon icon="check" />}
              />
            )}
            {(!surveyData.id || isOwner) && (
              <RegularButton
                disabled={saveDisabled || Boolean(surveyData.id && !name.trim())}
                onClick={onClickSave}
                textIntlId="surveyBuilder.save"
                defaultMessage="Save survey"
                icon={<Icon icon="check" />}
              />
            )}
          </div>
        </footer>
      </div>
    </LoaderWrapper>
  );
});

const mapStateToProps = ({
  user: { user },
  surveyBuilder: {
    surveyId,
    isSaving,
    surveyData,
    selectedQuestions,
    name,
    description,
    isNameTaken,
    isSaved,
    isChecking,
    questions
  }
}: IAppState) => ({
  surveyId,
  isSaving,
  surveyData,
  selectedQuestions,
  name,
  description: description || '',
  isNameTaken,
  isSaved,
  isChecking,
  user: user as User,
  questions
});

const mapDispatchToProps = {
  fetchSurvey: fetchSurveyRoutine,
  fetchAttributes: fetchAttributesRoutine,
  fetchDefaultQuestions: fetchDefaultQuestionsRoutine,
  saveSurvey: saveSurveyRoutine,
  changeName: changeSurveyNameRoutine,
  changeDescription: changeSurveyDescriptionRoutine,
  checkSurveyName: checkSurveyNameRoutine,
  fetchQuestions: fetchQuestionsRoutine
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(SurveyBuilder);
